import { ElementSelectorLogger } from '../types';

/**
 * Autogenerated-id pattern pack.
 *
 * When an element id matches any of these regexes, the algorithm treats the
 * element as if it has no id — the stableId strategy returns null and the
 * fallback walks past it instead of anchoring on it. Stops selectors from
 * pinning to ids that change between page loads or component re-mounts.
 *
 * Defaults are surfaced to customers in remote config so they can audit what's
 * being filtered and add or remove patterns to match their stack.
 */

/**
 * Built-in defaults, in the order they're applied (order doesn't affect
 * correctness — `some()` short-circuits on the first match).
 */
export const DEFAULT_AUTOGENERATED_ID_PATTERNS: ReadonlyArray<RegExp> = [
  // React useId() — ":r0:", ":r1:", ":rk:". Stable for one render; new id on next mount.
  /^:r[0-9a-z]+:$/,
  // Radix UI primitives — "radix-:r3:", "radix-A1B2C3". Prefix match (the
  // shape of what comes after `radix-` varies by Radix version and React
  // major; only the prefix is reliable).
  /^radix-/,
  // Headless UI (Tailwind Labs) — "headlessui-menu-button-:r5:". Prefix match.
  /^headlessui-/,
  // MUI internal id prefix — "mui-12345". Don't confuse with Mui-* CSS class state markers.
  /^mui-/,
  // Hex-only ids >=16 chars — UUIDs without hyphens, build hashes. Whole-string match.
  /^[a-f0-9]{16,}$/i,
  // Trailing long digit run — "session-1700000000", "row-20250515". Timestamps, counters.
  /-\d{8,}$/,
  // Any 4+ consecutive digits anywhere — "product-2134", "swiper-wrapper-…41039".
  // ContentSquare's heuristic; catches the digit-heavy portions of library-generated ids.
  /\d{4,}/,
];

/**
 * Compile a list of regex pattern strings (e.g. from a remote-config payload)
 * into RegExp objects. Invalid patterns are skipped (not thrown) so a single
 * bad regex in remote config can't crash the engine.
 *
 * Pass `logger` to surface invalid patterns at `warn` level — useful for
 * diagnosing why a customer's override isn't taking effect.
 *
 * Callers decide whether to merge the result with `DEFAULT_AUTOGENERATED_ID_PATTERNS`
 * or use it as a full replacement. That policy lives in the config resolver,
 * not here.
 */
export function compile(patterns: string[], logger?: ElementSelectorLogger): RegExp[] {
  const compiled: RegExp[] = [];
  for (const pattern of patterns) {
    try {
      compiled.push(new RegExp(pattern));
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      logger?.warn(`@amplitude/element-selector: ignoring invalid autogenerated-id pattern "${pattern}" (${message})`);
    }
  }
  return compiled;
}

/**
 * Check whether `id` is "stable" relative to a set of patterns. An id is
 * stable when it's a non-empty string AND doesn't match any pattern in
 * `patterns`. Null, undefined, and empty-string ids are not stable.
 */
export function isStableId(id: string | null | undefined, patterns: ReadonlyArray<RegExp>): boolean {
  if (id === null || id === undefined || id === '') return false;
  for (const pattern of patterns) {
    if (pattern.test(id)) return false;
  }
  return true;
}
